import { MMButton } from '@components'
import { Checkmark, XMark } from '@icons'
import cn from '@integrations/classnames'
import styles from '@requests/scss/limits.module.scss'
import { formatDisplayDate } from '@utils'
import {
  CustomerRoles,
  ExpenseRequestStatuses,
  isOwnerOrAdministrator,
  selectCurrentUserRole,
  useAppSelector
} from 'mmfintech-backend-api'
import type { ExpenseView } from 'mmfintech-backend-api/types'
import { formatMoney, tr } from 'mmfintech-commons'
import { MMTableView } from 'mmfintech-portal-commons'

interface Props {
  transaction: ExpenseView
  handleApprove: (requestId: number, sourceAccountId: number) => void
  handleReject: (requestId: number) => void
}

function RequestTableRow({ transaction, handleApprove, handleReject }: Props) {
  const { requestId, username, description, createdOn, amount, currency, status, sourceAccountId, bankAccountOut } =
    transaction || {}

  const customerRole = useAppSelector(selectCurrentUserRole)

  return (
    <MMTableView.Row data-test='table-row'>
      <MMTableView.Cell data-test='name'>{username}</MMTableView.Cell>
      <MMTableView.Cell className={cn(styles['request-description'], 'request-description')} data-test='description'>
        {description}
      </MMTableView.Cell>
      <MMTableView.Cell data-test='date'>{formatDisplayDate(createdOn)}</MMTableView.Cell>
      <MMTableView.Cell className={cn(styles['request-amount'], 'request-amount')} data-test='amount'>
        {formatMoney(amount, currency)}
      </MMTableView.Cell>
      <MMTableView.Cell className={cn('small')} data-test='recipient'>
        {bankAccountOut?.name}
      </MMTableView.Cell>
      <MMTableView.Cell className={cn(styles['action-buttons'], 'action-buttons')}>
        {status === ExpenseRequestStatuses.PENDING ? (
          <>
            {isOwnerOrAdministrator(customerRole) && (
              <MMButton
                type='button'
                variant='security'
                caption={tr('FRONTEND.EXPENSE_MANAGEMENT.BUTTONS.APPROVE', 'Approve')}
                onClick={() => handleApprove(requestId, sourceAccountId)}
                className={cn('approve')}
                data-test='approve-button'
              />
            )}
            <MMButton
              type='button'
              variant='security-delete'
              caption={
                customerRole === CustomerRoles.STAFF
                  ? tr('FRONTEND.EXPENSE_MANAGEMENT.BUTTONS.CANCEL', 'Cancel')
                  : tr('FRONTEND.EXPENSE_MANAGEMENT.BUTTONS.DECLINE', 'Decline')
              }
              onClick={() => handleReject(requestId)}
              className={cn('reject')}
              data-test='decline-button'
            />
          </>
        ) : (
          <div className={cn(styles['status'], 'status')}>
            <span className={cn(styles['icon'], 'icon')}>
              {status === ExpenseRequestStatuses.APPROVED ? <Checkmark /> : <XMark />}
            </span>
            {status}
          </div>
        )}
      </MMTableView.Cell>
    </MMTableView.Row>
  )
}

export default RequestTableRow
